import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle,
  DialogFooter,
  DialogDescription
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  Wand2, 
  Clock, 
  CheckCircle2, 
  Copy,
  XCircle,
  RotateCcw,
  Image as ImageIcon,
  Trash2,
  Eye,
  Loader2,
  ListOrdered,
  History,
  Settings2,
  Download,
  MapPin,
  Sun,
  Compass,
  Ruler,
  Palette,
  Sofa,
  Upload,
  Send,
  Sparkles
} from 'lucide-react';
import LotThumbnail from '../components/project/LotThumbnail';
import PlanCalibrator from '../components/project/PlanCalibrator';
import AdminGenerationGallery from '../components/generation/AdminGenerationGallery';
import { toast } from 'sonner';
import HUDPageHeader from '../components/HUDPageHeader';
import GenerationPendingNotice from '../components/materialtheque/GenerationPendingNotice';
import { CompassWidget } from '../components/CompassWidget';
import { RulerWidget } from '../components/RulerWidget';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULT_PARAMS = {
  // Projet
  type_bien: "Appartement",
  etage_niveau: "Étage intermédiaire",
  // Environnement
  localisation: "Paris",
  orientation_nord: 0,
  moment_journee: "Golden Hour",
  echelle_plan: "1/100",
  vue_angle: "Aérienne isométrique 45°",
  // Style
  style_design: "Contemporain",
  catalogue_mobilier: "Pack Standard",
  // Aménagements
  sols_principaux: "Parquet point de Hongrie chêne clair",
  sols_exterieurs: "Dalles de pierre grise",
  murs_principaux: "Peinture blanche mate",
  murs_accents: "",
  cuisine_materiaux: "Îlot central marbre blanc, façades bois clair",
  salle_manger_mobilier: "Table ovale bois massif, 6 chaises tissu clair",
  salon_mobilier: "Grand canapé d'angle velours côtelé blanc, table basse verre",
  chambre_principale: "Lit King size, tête de lit capitonnée, linge de lit lin lavé",
  mobilier_exterieur: "Salon de jardin rotin, 2 bains de soleil",
  vegetation_exterieure: "Grands oliviers en pots terre cuite",
  menuiserie: "Grandes baies vitrées aluminium noir mat",
  art_decoration: "Photographies d'art noir et blanc, céramiques artisanales",
  eclairage: "Suspensions en laiton, spots encastrés invisibles",
  branding_texte: "Image de synthèse - Simulation 3D non contractuelle"
};

const GenerationPage = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('gallery');
  const [queue, setQueue] = useState([]);
  const [history, setHistory] = useState([]);
  const [userRequests, setUserRequests] = useState([]);
  const [projects, setProjects] = useState([]);
  const [lots, setLots] = useState([]);
  const [files, setFiles] = useState([]);
  // Map lot_id → { count, last_completed_at } pour badge "déjà généré"
  const [lotGenerationsMap, setLotGenerationsMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [presets, setPresets] = useState({});
  const [options, setOptions] = useState({});
  
  // Dialogs
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showImageDialog, setShowImageDialog] = useState(false);
  const [showRegenerateDialog, setShowRegenerateDialog] = useState(false);
  
  // Selected items
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedLot, setSelectedLot] = useState(null);
  const [selectedPlanFile, setSelectedPlanFile] = useState(null);
  const [scaleMPerPx, setScaleMPerPx] = useState(null);  // optional plan calibration
  const [selectedJob, setSelectedJob] = useState(null);
  const [generatedImageUrl, setGeneratedImageUrl] = useState(null);

  // Batch mode (multi-lot generation)
  const [batchMode, setBatchMode] = useState(false);
  const [selectedLotIds, setSelectedLotIds] = useState(new Set());
  const [batchSubmitting, setBatchSubmitting] = useState(false);
  const [batchLotFilter, setBatchLotFilter] = useState('');
  
  // Reference images
  const [refSolFile, setRefSolFile] = useState(null);
  const [refMurFile, setRefMurFile] = useState(null);
  const [refMobilierFile, setRefMobilierFile] = useState(null);
  
  // Form
  const [params, setParams] = useState(DEFAULT_PARAMS);
  const [selectedPreset, setSelectedPreset] = useState('');

  useEffect(() => {
    if (user?.is_admin) {
      fetchData();
      const interval = setInterval(fetchQueue, 10000);
      return () => clearInterval(interval);
    }
  }, [user]);

  const fetchData = async () => {
    try {
      const [queueRes, historyRes, projectsRes, presetsRes, optionsRes, requestsRes] = await Promise.all([
        axios.get(`${API}/admin/generation/queue`, { withCredentials: true }),
        axios.get(`${API}/admin/generation/history?limit=50`, { withCredentials: true }),
        axios.get(`${API}/projects`, { withCredentials: true }),
        axios.get(`${API}/admin/generation/presets`, { withCredentials: true }),
        axios.get(`${API}/admin/generation/options`, { withCredentials: true }),
        axios.get(`${API}/admin/generation-requests`, { withCredentials: true })
      ]);
      setQueue(queueRes.data);
      setHistory(historyRes.data);
      setProjects(projectsRes.data);
      setPresets(presetsRes.data);
      setOptions(optionsRes.data);
      setUserRequests(requestsRes.data);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const fetchQueue = async () => {
    try {
      const [queueRes, historyRes] = await Promise.all([
        axios.get(`${API}/admin/generation/queue`, { withCredentials: true }),
        axios.get(`${API}/admin/generation/history?limit=50`, { withCredentials: true })
      ]);
      setQueue(queueRes.data);
      setHistory(historyRes.data);
    } catch (error) {
      console.error('Queue refresh failed');
    }
  };

  const fetchLots = async (projectId) => {
    try {
      const response = await axios.get(`${API}/projects/${projectId}/lots`, { withCredentials: true });
      setLots(response.data);
    } catch (error) {
      setLots([]);
    }
  };

  const fetchFiles = async (projectId) => {
    try {
      const response = await axios.get(`${API}/projects/${projectId}/files`, { withCredentials: true });
      const imageFiles = response.data.filter(f => f.content_type?.startsWith('image/'));
      setFiles(imageFiles);
    } catch (error) {
      setFiles([]);
    }
  };

  /**
   * Charge la liste des générations terminées du projet et construit une map
   * lot_id → { count, last_completed_at } pour afficher un badge "Déjà généré"
   * à côté des lots dans la dropdown.
   */
  const fetchLotGenerations = async (projectId) => {
    try {
      const response = await axios.get(`${API}/projects/${projectId}/generations`, { withCredentials: true });
      // Le endpoint retourne { items: [...], total, ... } (ou parfois un tableau brut — on gère les deux)
      const items = Array.isArray(response.data) ? response.data : (response.data?.items || []);
      const map = {};
      items.forEach((g) => {
        if (!g?.lot_id) return;
        if (!map[g.lot_id]) {
          map[g.lot_id] = { count: 0, last_completed_at: null };
        }
        map[g.lot_id].count += 1;
        const ts = g.completed_at || g.created_at;
        if (ts && (!map[g.lot_id].last_completed_at || ts > map[g.lot_id].last_completed_at)) {
          map[g.lot_id].last_completed_at = ts;
        }
      });
      setLotGenerationsMap(map);
    } catch {
      setLotGenerationsMap({});
    }
  };

  /**
   * Charge le prompt-config sauvegardé pour le projet (scope=project, fallback default)
   * et pré-remplit les `params` correspondants (matériaux, mobilier, style…).
   * Conserve les valeurs DEFAULT_PARAMS pour les champs non renseignés.
   */
  const fetchProjectPromptConfig = async (projectId, project) => {
    try {
      const r = await axios.get(`${API}/projects/${projectId}/prompt-config`, { withCredentials: true });
      const values = r?.data?.values || {};
      // Si project.address est renseigné, pré-remplir la localisation
      const localisation = project?.address?.trim() || values.localisation || DEFAULT_PARAMS.localisation;
      setParams((prev) => ({
        ...DEFAULT_PARAMS,
        ...prev,
        ...Object.fromEntries(Object.entries(values).filter(([, v]) => v !== null && v !== undefined && v !== '')),
        localisation,
      }));
    } catch {
      // Fallback : juste la localisation depuis project.address si dispo
      if (project?.address?.trim()) {
        setParams((prev) => ({ ...prev, localisation: project.address.trim() }));
      }
    }
  };

  const handleProjectSelect = (projectId) => {
    const project = projects.find(p => p.project_id === projectId);
    setSelectedProject(project);
    setSelectedLot(null);
    setSelectedPlanFile(null);
    setScaleMPerPx(null);
    setLotGenerationsMap({});
    setSelectedLotIds(new Set());
    setBatchLotFilter('');
    if (projectId) {
      fetchLots(projectId);
      fetchFiles(projectId);
      fetchLotGenerations(projectId);
      fetchProjectPromptConfig(projectId, project);
    }
  };

  /**
   * Quand l'utilisateur sélectionne un lot :
   *  - si le lot a `selected_plan_file_id` ET que ce fichier est dans `files`,
   *    on auto-sélectionne le plan (évite à l'utilisateur de re-choisir manuellement).
   */
  useEffect(() => {
    if (!selectedLot?.selected_plan_file_id) return;
    if (!files.length) return;
    const planFile = files.find((f) => f.file_id === selectedLot.selected_plan_file_id);
    if (planFile && selectedPlanFile?.file_id !== planFile.file_id) {
      setSelectedPlanFile(planFile);
      setScaleMPerPx(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLot, files]);

  const handlePresetSelect = (presetName) => {
    setSelectedPreset(presetName);
    if (presets[presetName]) {
      setParams({ ...DEFAULT_PARAMS, ...presets[presetName] });
    }
  };

  const handleCreateJob = async () => {
    if (!selectedLot || !selectedPlanFile) {
      toast.error('Sélectionnez un lot et un fichier plan');
      return;
    }

    try {
      const response = await axios.post(
        `${API}/admin/generation/create`,
        {
          lot_id: selectedLot.lot_id,
          plan_file_id: selectedPlanFile.file_id,
          params: params,
          priority: 0,
          ref_texture_sol_file_id: refSolFile?.file_id || null,
          ref_texture_mur_file_id: refMurFile?.file_id || null,
          ref_inspi_mobilier_file_id: refMobilierFile?.file_id || null,
          scale_m_per_px: scaleMPerPx,
        },
        { withCredentials: true }
      );
      toast.success('Génération lancée !');
      setShowCreateDialog(false);
      fetchQueue();
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    }
  };

  /**
   * Crée N générations en une seule requête. Params partagés (localisation, heure, angle…).
   * Le plan est résolu dans cet ordre : lot.selected_plan_file_id → selectedPlanFile (default).
   */
  const handleCreateBatch = async () => {
    const ids = Array.from(selectedLotIds);
    if (ids.length === 0) {
      toast.error('Sélectionnez au moins un lot');
      return;
    }
    setBatchSubmitting(true);
    try {
      const r = await axios.post(
        `${API}/admin/generation/create-batch`,
        {
          lot_ids: ids,
          params,
          priority: 0,
          default_plan_file_id: selectedPlanFile?.file_id || null,
          ref_texture_sol_file_id: refSolFile?.file_id || null,
          ref_texture_mur_file_id: refMurFile?.file_id || null,
          ref_inspi_mobilier_file_id: refMobilierFile?.file_id || null,
          scale_m_per_px: scaleMPerPx,
        },
        { withCredentials: true }
      );
      const { created = [], skipped = [], total_requested } = r.data || {};
      if (created.length > 0) {
        toast.success(
          `${created.length}/${total_requested} générations lancées`,
          {
            description: skipped.length
              ? `${skipped.length} lot(s) ignoré(s) (plan manquant)`
              : 'Toutes en file d\'attente',
          },
        );
      } else {
        toast.error(
          `Aucune génération lancée (${skipped.length} lot(s) ignoré(s))`,
          { description: 'Vérifie les plans rattachés aux lots' },
        );
      }
      setShowCreateDialog(false);
      fetchQueue();
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création batch');
    } finally {
      setBatchSubmitting(false);
    }
  };

  const resetForm = () => {
    setSelectedProject(null);
    setSelectedLot(null);
    setSelectedPlanFile(null);
    setScaleMPerPx(null);
    setRefSolFile(null);
    setRefMurFile(null);
    setRefMobilierFile(null);
    setParams(DEFAULT_PARAMS);
    setSelectedPreset('');
    setLotGenerationsMap({});
    setSelectedLotIds(new Set());
    setBatchLotFilter('');
  };

  const handleRegenerate = async () => {
    if (!selectedJob) return;

    try {
      await axios.post(
        `${API}/admin/generation/job/${selectedJob.job_id}/regenerate`,
        { 
          params: params,
          ref_texture_sol_file_id: refSolFile?.file_id || selectedJob.ref_texture_sol_file_id,
          ref_texture_mur_file_id: refMurFile?.file_id || selectedJob.ref_texture_mur_file_id,
          ref_inspi_mobilier_file_id: refMobilierFile?.file_id || selectedJob.ref_inspi_mobilier_file_id
        },
        { withCredentials: true }
      );
      toast.success('Régénération lancée !');
      setShowRegenerateDialog(false);
      fetchQueue();
    } catch (error) {
      toast.error('Erreur lors de la régénération');
    }
  };

  const handleCancelJob = async (jobId) => {
    try {
      await axios.delete(`${API}/admin/generation/job/${jobId}`, { withCredentials: true });
      toast.success('Job annulé');
      fetchQueue();
    } catch (error) {
      toast.error('Impossible d\'annuler');
    }
  };

  const handleViewImage = async (job) => {
    if (!job.generated_image_path) return;
    
    try {
      const response = await axios.get(
        `${API}/admin/generation/job/${job.job_id}/image`,
        { withCredentials: true, responseType: 'blob' }
      );
      const url = URL.createObjectURL(response.data);
      setGeneratedImageUrl(url);
      setSelectedJob(job);
      setShowImageDialog(true);
    } catch (error) {
      toast.error('Erreur lors du chargement de l\'image');
    }
  };

  const handleDownloadImage = async (job) => {
    try {
      const response = await axios.get(
        `${API}/admin/generation/job/${job.job_id}/image`,
        { withCredentials: true, responseType: 'blob' }
      );
      const url = URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = `shoebox_${job.lot_id}_${job.job_id}.png`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Erreur lors du téléchargement');
    }
  };

  const openRegenerateDialog = (job) => {
    setSelectedJob(job);
    setParams(job.params || DEFAULT_PARAMS);
    setShowRegenerateDialog(true);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return <CheckCircle2 className="w-4 h-4 text-green-600" />;
      case 'failed': return <XCircle className="w-4 h-4 text-red-600" />;
      case 'processing': return <Loader2 className="w-4 h-4 text-blue-600 animate-spin" />;
      case 'cancelled': return <XCircle className="w-4 h-4 text-gray-400" />;
      default: return <Clock className="w-4 h-4 text-amber-600" />;
    }
  };

  const getStatusLabel = (status) => {
    const labels = {
      pending: 'En attente',
      processing: 'En cours',
      completed: 'Terminé',
      failed: 'Échec',
      cancelled: 'Annulé'
    };
    return labels[status] || status;
  };

  const handleUpdateRequestStatus = async (requestId, newStatus) => {
    try {
      await axios.put(`${API}/admin/generation-requests/${requestId}/status`, { status: newStatus }, { withCredentials: true });
      toast.success('Statut mis à jour');
      setUserRequests(prev => prev.map(r => r.request_id === requestId ? {...r, status: newStatus} : r));
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleDeliverImages = async (requestId) => {
    const completedJobs = history.filter(j => j.status === 'completed' && j.generated_image_path);
    if (completedJobs.length === 0) {
      toast.error("Aucune image générée disponible. Lancez d'abord une génération.");
      return;
    }
    const jobIds = completedJobs.slice(0, 5).map(j => j.job_id);
    try {
      await axios.post(`${API}/admin/generation-requests/${requestId}/deliver`, { job_ids: jobIds }, { withCredentials: true });
      toast.success('Images livrées avec succès !');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const getRequestStatusBadge = (status) => {
    const map = {
      pending: 'bg-amber-100 text-amber-700',
      processing: 'bg-blue-100 text-blue-700',
      completed: 'bg-green-100 text-green-700',
      delivered: 'bg-purple-100 text-purple-700'
    };
    return map[status] || 'bg-gray-100 text-gray-700';
  };

  const pendingRequests = userRequests.filter(r => r.status === 'pending').length;

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!user?.is_admin) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-gray-500">Accès réservé aux administrateurs</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
      </div>
    );
  }

  return (
    <div data-hud-page data-testid="generation-page" className="fade-in">
      <HUDPageHeader
        module="Admin"
        breadcrumb={[{ label: 'Hub', to: '/studio/dashboard' }, { label: 'Génération' }]}
        icon={Wand2}
        title="Génération Shoebox"
        subtitle="Interface de génération IA (Nano Banana 2 Pro)"
        actions={(
          <button
            onClick={() => setShowCreateDialog(true)}
            data-testid="new-generation-btn"
            className="hud-btn hud-btn-primary hud-btn-md"
          >
            <Wand2 className="w-4 h-4" />
            Nouvelle génération
          </button>
        )}
      />
      <div className="px-4 sm:px-6 lg:px-8 py-6">
        <GenerationPendingNotice />

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-gray-100 p-1 mb-6">
          <TabsTrigger value="gallery" data-testid="tab-gallery">
            <ImageIcon className="w-4 h-4 mr-2" />
            Galerie
          </TabsTrigger>
          <TabsTrigger value="requests" data-testid="tab-user-requests">
            <Send className="w-4 h-4 mr-2" />
            Demandes
            {pendingRequests > 0 && (
              <span className="ml-1.5 text-[10px] bg-amber-500 text-white rounded-full px-1.5 py-0.5 font-bold">{pendingRequests}</span>
            )}
          </TabsTrigger>
          <TabsTrigger value="queue">
            <ListOrdered className="w-4 h-4 mr-2" />
            File d'attente ({queue.length})
          </TabsTrigger>
          <TabsTrigger value="history">
            <History className="w-4 h-4 mr-2" />
            Historique
          </TabsTrigger>
        </TabsList>

        {/* Gallery Tab (new admin tree+filters view) */}
        <TabsContent value="gallery">
          <AdminGenerationGallery user={user} />
        </TabsContent>

        {/* User Requests Tab */}
        <TabsContent value="requests">
          {userRequests.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
              <Send className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">Aucune demande utilisateur</p>
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="overflow-x-auto">
              <table className="w-full min-w-[700px]" data-testid="user-requests-table">
                <thead>
                  <tr className="border-b border-gray-100 bg-gray-50/50">
                    <th className="text-left p-3 text-xs font-medium text-gray-500 uppercase">Utilisateur</th>
                    <th className="text-left p-3 text-xs font-medium text-gray-500 uppercase">Projet / Lot</th>
                    <th className="text-left p-3 text-xs font-medium text-gray-500 uppercase">Type / Style</th>
                    <th className="text-left p-3 text-xs font-medium text-gray-500 uppercase">Statut</th>
                    <th className="text-left p-3 text-xs font-medium text-gray-500 uppercase">Date</th>
                    <th className="text-right p-3 text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {userRequests.map(req => (
                    <tr key={req.request_id} className="border-b border-gray-50 hover:bg-gray-50/50">
                      <td className="p-3">
                        <p className="text-sm font-medium text-gray-900">{req.user_name}</p>
                        <p className="text-xs text-gray-500">{req.user_email}</p>
                      </td>
                      <td className="p-3">
                        <p className="text-sm text-gray-900">{req.project_name}</p>
                        <p className="text-xs text-gray-500">{req.lot_name}</p>
                      </td>
                      <td className="p-3">
                        <p className="text-sm text-gray-700">{req.type_bien}</p>
                        <p className="text-xs text-gray-500">{req.style}</p>
                      </td>
                      <td className="p-3">
                        <span className={`text-xs px-2 py-1 rounded-full font-medium ${getRequestStatusBadge(req.status)}`}>
                          {getStatusLabel(req.status) || req.status}
                        </span>
                      </td>
                      <td className="p-3 text-sm text-gray-500">{formatDate(req.created_at)}</td>
                      <td className="p-3">
                        <div className="flex items-center justify-end gap-1">
                          {req.status === 'pending' && (
                            <Button size="sm" variant="outline" onClick={() => handleUpdateRequestStatus(req.request_id, 'processing')}>
                              <Loader2 className="w-3 h-3 mr-1" />Traiter
                            </Button>
                          )}
                          {req.status === 'processing' && (
                            <Button size="sm" variant="outline" onClick={() => handleUpdateRequestStatus(req.request_id, 'completed')}>
                              <CheckCircle2 className="w-3 h-3 mr-1" />Terminé
                            </Button>
                          )}
                          {(req.status === 'completed' || req.status === 'processing') && (
                            <Button size="sm" className="bg-purple-600 hover:bg-purple-700 text-white" onClick={() => handleDeliverImages(req.request_id)} data-testid={`deliver-${req.request_id}`}>
                              <ImageIcon className="w-3 h-3 mr-1" />Livrer
                            </Button>
                          )}
                          {req.status === 'delivered' && (
                            <span className="text-xs text-purple-600 font-medium flex items-center gap-1">
                              <CheckCircle2 className="w-3 h-3" />
                              {req.delivered_images?.length} image(s)
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            </div>
          )}
        </TabsContent>

        {/* Queue Tab */}
        <TabsContent value="queue">
          {queue.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
              <Clock className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">Aucune génération en cours</p>
            </div>
          ) : (
            <div className="space-y-3">
              {queue.map((job, index) => (
                <div 
                  key={job.job_id}
                  className="bg-white rounded-xl border border-gray-200 p-4 flex items-center gap-4"
                >
                  <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center text-gray-600 font-medium">
                    {index + 1}
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900">Lot: {job.lot_id}</p>
                    <p className="text-sm text-gray-500">
                      {job.params?.style_design} • {job.params?.localisation} • {job.params?.moment_journee}
                    </p>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    {getStatusIcon(job.status)}
                    <span className="text-sm text-gray-600">{getStatusLabel(job.status)}</span>
                  </div>
                  
                  {job.status === 'pending' && (
                    <Button 
                      variant="ghost" 
                      size="icon"
                      className="text-red-600 hover:bg-red-50"
                      onClick={() => handleCancelJob(job.job_id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* History Tab */}
        <TabsContent value="history">
          {history.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
              <History className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">Aucun historique</p>
            </div>
          ) : (
            <div className="space-y-3">
              {history.map((job) => (
                <div 
                  key={job.job_id}
                  className="bg-white rounded-xl border border-gray-200 p-4 flex items-center gap-4"
                >
                  <div className="w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center overflow-hidden">
                    {job.status === 'completed' && job.generated_image_path ? (
                      <ImageIcon className="w-6 h-6 text-blue-600" />
                    ) : (
                      <ImageIcon className="w-6 h-6 text-gray-300" />
                    )}
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      {getStatusIcon(job.status)}
                      <p className="font-medium text-gray-900">{getStatusLabel(job.status)}</p>
                    </div>
                    <p className="text-sm text-gray-500">
                      {job.params?.type_bien} • {job.params?.style_design} • {job.params?.localisation}
                    </p>
                    <p className="text-xs text-gray-400">
                      {formatDate(job.created_at)}
                      {job.generation_time_seconds && ` • ${job.generation_time_seconds}s`}
                    </p>
                  </div>
                  
                  {job.error_message && (
                    <p className="text-xs text-red-600 max-w-xs truncate" title={job.error_message}>
                      {job.error_message}
                    </p>
                  )}
                  
                  <div className="flex items-center gap-1">
                    {job.status === 'completed' && job.generated_image_path && (
                      <>
                        <Button variant="ghost" size="icon" onClick={() => handleViewImage(job)} title="Voir">
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => handleDownloadImage(job)} title="Télécharger">
                          <Download className="w-4 h-4" />
                        </Button>
                      </>
                    )}
                    <Button variant="ghost" size="icon" onClick={() => openRegenerateDialog(job)} title="Régénérer">
                      <RotateCcw className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Create Generation Dialog - Full Form */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Nouvelle génération Shoebox</DialogTitle>
            <DialogDescription>
              Configurez tous les paramètres pour la génération 3D
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Mode toggle: Single lot vs Batch (multi-lot) */}
            <div className="flex items-center justify-between bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-amber-100 flex items-center justify-center">
                  <Sparkles className="w-4 h-4 text-amber-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-amber-900">
                    {batchMode ? 'Mode multi-lot (batch)' : 'Mode lot unique'}
                  </p>
                  <p className="text-[11.5px] text-amber-700">
                    {batchMode
                      ? 'Lance N générations en parallèle avec les mêmes paramètres'
                      : 'Une seule génération sur un lot'}
                  </p>
                </div>
              </div>
              <label className="flex items-center gap-2 cursor-pointer select-none" data-testid="gen-batch-mode-toggle-wrapper">
                <span className="text-[12px] font-semibold text-amber-800">Multi-lot</span>
                <input
                  type="checkbox"
                  checked={batchMode}
                  onChange={(e) => {
                    setBatchMode(e.target.checked);
                    if (e.target.checked) {
                      // Re-init selected single lot to avoid conflicts
                      setSelectedLot(null);
                    } else {
                      setSelectedLotIds(new Set());
                    }
                  }}
                  className="w-4 h-4 accent-amber-600"
                  data-testid="gen-batch-mode-toggle"
                />
              </label>
            </div>

            {/* Section 1: Projet & Fichiers */}
            <div className="bg-gray-50 rounded-xl p-4">
              <h3 className="font-medium text-gray-900 mb-4 flex items-center gap-2">
                <Upload className="w-4 h-4" />
                1. Données du Projet & Fichier Source
              </h3>
              
              <div className="grid grid-cols-3 gap-4 mb-4">
                <div>
                  <label className="text-sm font-medium text-gray-700 block mb-2">Projet *</label>
                  <Select value={selectedProject?.project_id || ''} onValueChange={handleProjectSelect}>
                    <SelectTrigger><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                    <SelectContent>
                      {projects.map(p => (
                        <SelectItem key={p.project_id} value={p.project_id}>{p.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 block mb-2">
                    {batchMode ? `Lots sélectionnés (${selectedLotIds.size}/${lots.length})` : 'Lot *'}
                  </label>
                  {batchMode ? (
                    <BatchLotPicker
                      lots={lots}
                      filter={batchLotFilter}
                      onFilterChange={setBatchLotFilter}
                      selectedIds={selectedLotIds}
                      onChange={setSelectedLotIds}
                      lotGenerationsMap={lotGenerationsMap}
                      disabled={!selectedProject}
                    />
                  ) : (
                    <>
                      <Select value={selectedLot?.lot_id || ''} onValueChange={(v) => setSelectedLot(lots.find(l => l.lot_id === v))} disabled={!selectedProject}>
                        <SelectTrigger><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                        <SelectContent>
                          {lots.map(l => {
                            const genInfo = lotGenerationsMap[l.lot_id];
                            const alreadyGenerated = !!(genInfo && genInfo.count > 0);
                            return (
                              <SelectItem key={l.lot_id} value={l.lot_id} data-testid={`gen-lot-option-${l.lot_id}`}>
                                <span className="flex items-center gap-2">
                                  <LotThumbnail lot={l} size={24} />
                                  <span>{l.lot_number} - {l.typology}</span>
                                  {alreadyGenerated && (
                                    <span
                                      className="inline-flex items-center gap-0.5 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700"
                                      title={`Déjà généré ${genInfo.count}×`}
                                      data-testid={`gen-lot-badge-${l.lot_id}`}
                                    >
                                      <CheckCircle2 className="w-2.5 h-2.5" />
                                      {genInfo.count > 1 ? `${genInfo.count}×` : 'fait'}
                                    </span>
                                  )}
                                </span>
                              </SelectItem>
                            );
                          })}
                        </SelectContent>
                      </Select>
                      {selectedLot && lotGenerationsMap[selectedLot.lot_id]?.count > 0 && (
                        <p className="mt-1 text-[11px] text-emerald-600 flex items-center gap-1" data-testid="gen-lot-selected-status">
                          <CheckCircle2 className="w-3 h-3" />
                          Ce lot a déjà {lotGenerationsMap[selectedLot.lot_id].count} génération{lotGenerationsMap[selectedLot.lot_id].count > 1 ? 's' : ''} terminée{lotGenerationsMap[selectedLot.lot_id].count > 1 ? 's' : ''}
                        </p>
                      )}
                    </>
                  )}
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 block mb-2">Type de bien</label>
                  <Select value={params.type_bien} onValueChange={(v) => setParams({...params, type_bien: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {(options.type_bien || []).map(t => (
                        <SelectItem key={t} value={t}>{t}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-700 block mb-2">
                    {batchMode ? 'Plan par défaut (image)' : 'Fichier Plan (image) *'}
                  </label>
                  {batchMode && (
                    <p className="text-[10.5px] text-amber-700 mb-1.5">
                      Utilisé uniquement pour les lots sans plan rattaché. Sinon le plan du lot est repris automatiquement.
                    </p>
                  )}
                  {files.length === 0 ? (
                    <p className="text-sm text-gray-500 p-3 bg-white rounded border">Aucun fichier disponible</p>
                  ) : (
                    <div className="grid grid-cols-2 gap-2 max-h-32 overflow-y-auto">
                      {files.map(f => {
                        const isLotPlan = selectedLot?.selected_plan_file_id === f.file_id;
                        return (
                          <div 
                            key={f.file_id}
                            onClick={() => { setSelectedPlanFile(f); setScaleMPerPx(null); }}
                            className={`p-2 rounded-lg border-2 cursor-pointer text-xs ${
                              selectedPlanFile?.file_id === f.file_id 
                                ? 'border-blue-600 bg-blue-50' 
                                : 'border-gray-200 hover:border-blue-300 bg-white'
                            }`}
                            data-testid={`gen-plan-file-${f.file_id}`}
                          >
                            <ImageIcon className="w-4 h-4 text-gray-400 inline mr-1" />
                            <span className="truncate">{f.original_filename}</span>
                            {isLotPlan && (
                              <span
                                className="ml-1 inline-flex items-center gap-0.5 text-[9px] font-semibold px-1 py-0.5 rounded-full bg-amber-100 text-amber-700"
                                title="Plan rattaché à ce lot"
                                data-testid={`gen-plan-attached-${f.file_id}`}
                              >
                                <CheckCircle2 className="w-2.5 h-2.5" />
                                lot
                              </span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 block mb-2">Niveau d'étage</label>
                  <Select value={params.etage_niveau} onValueChange={(v) => setParams({...params, etage_niveau: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {(options.etage_niveau || []).map(t => (
                        <SelectItem key={t} value={t}>{t}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Plan calibration — optional but recommended */}
              {selectedPlanFile && (
                <div className="mt-4">
                  <PlanCalibrator
                    key={selectedPlanFile.file_id}
                    planUrl={`${API}/files/${selectedPlanFile.file_id}/download`}
                    testidPrefix="shoebox-calib"
                    compact
                    onCalibrated={({ scale_m_per_px }) => setScaleMPerPx(scale_m_per_px)}
                    onReset={() => setScaleMPerPx(null)}
                  />
                  {scaleMPerPx != null && (
                    <p className="mt-2 text-[11px] text-emerald-600 font-medium flex items-center gap-1">
                      ✓ Calibration injectée dans le prompt — proportions verrouillées au mètre.
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Section 2: Environnement */}
            <div className="bg-gray-50 rounded-xl p-4">
              <h3 className="font-medium text-gray-900 mb-4 flex items-center gap-2">
                <Sun className="w-4 h-4" />
                2. Environnement, Lumière & Géométrie
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-6 items-start">
                {/* Left column */}
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-gray-700 block mb-2">
                      <MapPin className="w-3 h-3 inline mr-1" />
                      Localisation
                    </label>
                    <Input value={params.localisation} onChange={(e) => setParams({...params, localisation: e.target.value})} placeholder="Paris, Marrakech..." />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700 block mb-2">Moment de la journée</label>
                    <Select value={params.moment_journee} onValueChange={(v) => setParams({...params, moment_journee: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {(options.moment_journee || []).map(t => (
                          <SelectItem key={t} value={t}>{t}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700 block mb-2">Angle de vue</label>
                    <Select value={params.vue_angle} onValueChange={(v) => setParams({...params, vue_angle: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {(options.vue_angle || []).map(t => (
                          <SelectItem key={t} value={t}>
                            <span className="inline-flex items-center gap-1.5">
                              <span>{t}</span>
                              {t === 'Aérienne isométrique 45°' && (
                                <span className="text-[9px] font-semibold uppercase tracking-wider px-1 py-0.5 rounded bg-emerald-500/15 text-emerald-400 border border-emerald-500/30">
                                  recommandé
                                </span>
                              )}
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Center - Compass Widget */}
                <div className="flex flex-col items-center order-first md:order-none">
                  <label className="text-sm font-medium text-gray-700 block mb-2">
                    <Compass className="w-3 h-3 inline mr-1" />
                    Orientation Nord
                  </label>
                  <CompassWidget
                    value={params.orientation_nord}
                    onChange={(v) => setParams({...params, orientation_nord: v})}
                    size={200}
                    momentJournee={params.moment_journee}
                  />
                </div>

                {/* Right column - Ruler Widget */}
                <div>
                  <label className="text-sm font-medium text-gray-700 block mb-2">
                    <Ruler className="w-3 h-3 inline mr-1" />
                    Échelle du plan
                  </label>
                  <RulerWidget
                    value={params.echelle_plan}
                    onChange={(v) => setParams({...params, echelle_plan: v})}
                  />
                </div>
              </div>
            </div>

            {/* Section 3: Style & Moodboard */}
            <div className="bg-gray-50 rounded-xl p-4">
              <h3 className="font-medium text-gray-900 mb-4 flex items-center gap-2">
                <Palette className="w-4 h-4" />
                3. Style Global & Matériauthèque
              </h3>
              
              <div className="grid grid-cols-3 gap-4 mb-4">
                <div>
                  <label className="text-sm font-medium text-gray-700 block mb-2">Style de design</label>
                  <Select value={params.style_design} onValueChange={(v) => { setParams({...params, style_design: v}); handlePresetSelect(v.toLowerCase().replace(' ', '_')); }}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {(options.style_design || []).map(t => (
                        <SelectItem key={t} value={t}>{t}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 block mb-2">Catalogue mobilier</label>
                  <Select value={params.catalogue_mobilier} onValueChange={(v) => setParams({...params, catalogue_mobilier: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {(options.catalogue_mobilier || []).map(t => (
                        <SelectItem key={t} value={t}>{t}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="text-sm text-gray-600 block mb-2">Réf. texture sol (optionnel)</label>
                  <Select value={refSolFile?.file_id || 'none'} onValueChange={(v) => setRefSolFile(v === 'none' ? null : files.find(f => f.file_id === v))}>
                    <SelectTrigger className="text-xs"><SelectValue placeholder="Aucune" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Aucune</SelectItem>
                      {files.map(f => (
                        <SelectItem key={f.file_id} value={f.file_id}>{f.original_filename}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm text-gray-600 block mb-2">Réf. texture mur (optionnel)</label>
                  <Select value={refMurFile?.file_id || 'none'} onValueChange={(v) => setRefMurFile(v === 'none' ? null : files.find(f => f.file_id === v))}>
                    <SelectTrigger className="text-xs"><SelectValue placeholder="Aucune" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Aucune</SelectItem>
                      {files.map(f => (
                        <SelectItem key={f.file_id} value={f.file_id}>{f.original_filename}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm text-gray-600 block mb-2">Réf. style mobilier (optionnel)</label>
                  <Select value={refMobilierFile?.file_id || 'none'} onValueChange={(v) => setRefMobilierFile(v === 'none' ? null : files.find(f => f.file_id === v))}>
                    <SelectTrigger className="text-xs"><SelectValue placeholder="Aucune" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Aucune</SelectItem>
                      {files.map(f => (
                        <SelectItem key={f.file_id} value={f.file_id}>{f.original_filename}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Section 4: Aménagements */}
            <details className="bg-gray-50 rounded-xl p-4">
              <summary className="font-medium text-gray-900 cursor-pointer flex items-center gap-2">
                <Sofa className="w-4 h-4" />
                4. Aménagements Détaillés (cliquez pour ouvrir)
              </summary>
              
              <div className="mt-4 grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-gray-600 block mb-1">Sols principaux</label>
                  <Input value={params.sols_principaux} onChange={(e) => setParams({...params, sols_principaux: e.target.value})} />
                </div>
                <div>
                  <label className="text-xs text-gray-600 block mb-1">Sols extérieurs</label>
                  <Input value={params.sols_exterieurs} onChange={(e) => setParams({...params, sols_exterieurs: e.target.value})} />
                </div>
                <div>
                  <label className="text-xs text-gray-600 block mb-1">Murs principaux</label>
                  <Input value={params.murs_principaux} onChange={(e) => setParams({...params, murs_principaux: e.target.value})} />
                </div>
                <div>
                  <label className="text-xs text-gray-600 block mb-1">Murs accents</label>
                  <Input value={params.murs_accents} onChange={(e) => setParams({...params, murs_accents: e.target.value})} placeholder="Ex: Mur bleu majorelle dans le salon" />
                </div>
                <div>
                  <label className="text-xs text-gray-600 block mb-1">Cuisine (matériaux)</label>
                  <Input value={params.cuisine_materiaux} onChange={(e) => setParams({...params, cuisine_materiaux: e.target.value})} />
                </div>
                <div>
                  <label className="text-xs text-gray-600 block mb-1">Salle à manger</label>
                  <Input value={params.salle_manger_mobilier} onChange={(e) => setParams({...params, salle_manger_mobilier: e.target.value})} />
                </div>
                <div>
                  <label className="text-xs text-gray-600 block mb-1">Salon</label>
                  <Input value={params.salon_mobilier} onChange={(e) => setParams({...params, salon_mobilier: e.target.value})} />
                </div>
                <div>
                  <label className="text-xs text-gray-600 block mb-1">Chambre principale</label>
                  <Input value={params.chambre_principale} onChange={(e) => setParams({...params, chambre_principale: e.target.value})} />
                </div>
                <div>
                  <label className="text-xs text-gray-600 block mb-1">Mobilier extérieur</label>
                  <Input value={params.mobilier_exterieur} onChange={(e) => setParams({...params, mobilier_exterieur: e.target.value})} />
                </div>
                <div>
                  <label className="text-xs text-gray-600 block mb-1">Végétation extérieure</label>
                  <Input value={params.vegetation_exterieure} onChange={(e) => setParams({...params, vegetation_exterieure: e.target.value})} />
                </div>
                <div>
                  <label className="text-xs text-gray-600 block mb-1">Menuiseries</label>
                  <Input value={params.menuiserie} onChange={(e) => setParams({...params, menuiserie: e.target.value})} />
                </div>
                <div>
                  <label className="text-xs text-gray-600 block mb-1">Art & décoration</label>
                  <Input value={params.art_decoration} onChange={(e) => setParams({...params, art_decoration: e.target.value})} />
                </div>
                <div>
                  <label className="text-xs text-gray-600 block mb-1">Éclairage</label>
                  <Input value={params.eclairage} onChange={(e) => setParams({...params, eclairage: e.target.value})} />
                </div>
                <div>
                  <label className="text-xs text-gray-600 block mb-1">Texte branding</label>
                  <Input value={params.branding_texte} onChange={(e) => setParams({...params, branding_texte: e.target.value})} />
                </div>
              </div>
            </details>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Annuler</Button>
            {batchMode ? (
              <Button
                onClick={handleCreateBatch}
                disabled={batchSubmitting || selectedLotIds.size === 0}
                className="bg-amber-600 hover:bg-amber-700"
                data-testid="gen-batch-submit"
              >
                <Wand2 className="w-4 h-4 mr-2" />
                {batchSubmitting
                  ? 'Lancement en cours…'
                  : `Lancer ${selectedLotIds.size} génération${selectedLotIds.size > 1 ? 's' : ''}`}
              </Button>
            ) : (
              <Button
                onClick={handleCreateJob}
                disabled={!selectedLot || !selectedPlanFile}
                className="bg-blue-600 hover:bg-blue-700"
              >
                <Wand2 className="w-4 h-4 mr-2" />
                Lancer la génération
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Image Preview Dialog */}
      <Dialog open={showImageDialog} onOpenChange={setShowImageDialog}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>Image générée</DialogTitle>
          </DialogHeader>
          <div className="flex items-center justify-center bg-gray-100 rounded-lg p-4">
            {generatedImageUrl && (
              <img src={generatedImageUrl} alt="Generated Shoebox" className="max-w-full max-h-[60vh] object-contain rounded-lg" />
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowImageDialog(false)}>Fermer</Button>
            {selectedJob && (
              <>
                <Button variant="outline" onClick={() => handleDownloadImage(selectedJob)}>
                  <Download className="w-4 h-4 mr-2" />Télécharger
                </Button>
                <Button onClick={() => { setShowImageDialog(false); openRegenerateDialog(selectedJob); }}>
                  <RotateCcw className="w-4 h-4 mr-2" />Régénérer
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Regenerate Dialog */}
      <Dialog open={showRegenerateDialog} onOpenChange={setShowRegenerateDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Régénérer avec nouveaux paramètres</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-gray-600 block mb-1">Style</label>
                <Select value={params.style_design} onValueChange={(v) => setParams({...params, style_design: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {(options.style_design || []).map(t => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-gray-600 block mb-1">Moment journée</label>
                <Select value={params.moment_journee} onValueChange={(v) => setParams({...params, moment_journee: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {(options.moment_journee || []).map(t => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-gray-600 block mb-1">Angle de vue</label>
                <Select value={params.vue_angle} onValueChange={(v) => setParams({...params, vue_angle: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {(options.vue_angle || []).map(t => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-gray-600 block mb-1">Localisation</label>
                <Input value={params.localisation} onChange={(e) => setParams({...params, localisation: e.target.value})} />
              </div>
              <div>
                <label className="text-xs text-gray-600 block mb-1">Sols</label>
                <Input value={params.sols_principaux} onChange={(e) => setParams({...params, sols_principaux: e.target.value})} />
              </div>
              <div>
                <label className="text-xs text-gray-600 block mb-1">Murs</label>
                <Input value={params.murs_principaux} onChange={(e) => setParams({...params, murs_principaux: e.target.value})} />
              </div>
              <div>
                <label className="text-xs text-gray-600 block mb-1">Salon</label>
                <Input value={params.salon_mobilier} onChange={(e) => setParams({...params, salon_mobilier: e.target.value})} />
              </div>
              <div>
                <label className="text-xs text-gray-600 block mb-1">Chambre</label>
                <Input value={params.chambre_principale} onChange={(e) => setParams({...params, chambre_principale: e.target.value})} />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRegenerateDialog(false)}>Annuler</Button>
            <Button onClick={handleRegenerate} className="bg-blue-600 hover:bg-blue-700">
              <RotateCcw className="w-4 h-4 mr-2" />Régénérer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </div>
    </div>
  );
};

// ============================================================================
// BatchLotPicker — checklist scrollable + filtre + actions bulk pour batch mode
// ============================================================================
const BatchLotPicker = ({ lots, filter, onFilterChange, selectedIds, onChange, lotGenerationsMap, disabled }) => {
  const filtered = lots.filter((l) => {
    if (!filter) return true;
    const q = filter.toLowerCase();
    return (l.lot_number || '').toLowerCase().includes(q)
      || (l.name || '').toLowerCase().includes(q)
      || (l.typology || '').toLowerCase().includes(q);
  });

  const allFilteredSelected = filtered.length > 0 && filtered.every((l) => selectedIds.has(l.lot_id));

  const toggle = (lotId) => {
    const next = new Set(selectedIds);
    if (next.has(lotId)) next.delete(lotId);
    else next.add(lotId);
    onChange(next);
  };

  const selectAllFiltered = () => {
    const next = new Set(selectedIds);
    filtered.forEach((l) => next.add(l.lot_id));
    onChange(next);
  };

  const deselectAllFiltered = () => {
    const next = new Set(selectedIds);
    filtered.forEach((l) => next.delete(l.lot_id));
    onChange(next);
  };

  const selectAllUngenerated = () => {
    const next = new Set(selectedIds);
    filtered.forEach((l) => {
      const gens = lotGenerationsMap?.[l.lot_id]?.count || 0;
      if (!gens) next.add(l.lot_id);
    });
    onChange(next);
  };

  if (disabled) {
    return <p className="text-xs text-gray-400 italic p-2">Sélectionnez d'abord un projet…</p>;
  }
  if (lots.length === 0) {
    return <p className="text-xs text-gray-400 italic p-2">Aucun lot dans ce projet.</p>;
  }

  return (
    <div className="space-y-1.5" data-testid="batch-lot-picker">
      <div className="flex items-center gap-1.5">
        <Input
          value={filter}
          onChange={(e) => onFilterChange(e.target.value)}
          placeholder="Filtrer (numéro, nom, typologie…)"
          className="h-8 text-xs flex-1"
          data-testid="batch-lot-filter"
        />
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-8 text-[10.5px] px-2"
          onClick={allFilteredSelected ? deselectAllFiltered : selectAllFiltered}
          data-testid="batch-lot-select-all"
        >
          {allFilteredSelected ? 'Aucun' : `Tous (${filtered.length})`}
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-8 text-[10.5px] px-2 border-amber-300 text-amber-700 hover:bg-amber-50"
          onClick={selectAllUngenerated}
          title="Cocher tous les lots non encore générés"
          data-testid="batch-lot-select-ungenerated"
        >
          Non générés
        </Button>
      </div>
      <div
        className="max-h-44 overflow-y-auto rounded-lg border border-gray-200 bg-white divide-y divide-gray-100"
        data-testid="batch-lot-list"
      >
        {filtered.length === 0 ? (
          <p className="text-xs text-gray-400 italic p-2.5">Aucun lot ne correspond.</p>
        ) : (
          filtered.map((l) => {
            const gens = lotGenerationsMap?.[l.lot_id];
            const checked = selectedIds.has(l.lot_id);
            const hasPlan = !!l.selected_plan_file_id;
            return (
              <label
                key={l.lot_id}
                className={`flex items-center gap-2 px-2.5 py-1.5 cursor-pointer text-xs transition-colors ${checked ? 'bg-blue-50' : 'hover:bg-gray-50'}`}
                data-testid={`batch-lot-item-${l.lot_id}`}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggle(l.lot_id)}
                  className="accent-amber-600"
                  data-testid={`batch-lot-checkbox-${l.lot_id}`}
                />
                <LotThumbnail lot={l} size={28} />
                <span className="font-medium text-gray-800 flex-shrink-0">{l.lot_number}</span>
                <span className="text-gray-500 flex-shrink-0">·</span>
                <span className="text-gray-600 flex-1 truncate" title={l.name}>
                  {l.typology}{l.name && l.name !== l.lot_number ? ` — ${l.name}` : ''}
                </span>
                {!hasPlan && (
                  <span
                    className="inline-flex items-center gap-0.5 text-[9.5px] font-semibold px-1 py-0.5 rounded-full bg-orange-100 text-orange-700 flex-shrink-0"
                    title="Aucun plan rattaché — utilisera le plan par défaut sélectionné en bas"
                  >
                    plan défaut
                  </span>
                )}
                {/* Iter 82 — Correspondance pill: warns when this lot duplicates another */}
                {l.correspondance && l.correspondance.toLowerCase() !== 'unique' && (
                  <span
                    className="inline-flex items-center gap-0.5 text-[9.5px] font-semibold px-1 py-0.5 rounded-full bg-sky-100 text-sky-700 flex-shrink-0"
                    title={`Similaire au lot ${l.correspondance} — partage son shoebox ; la génération séparée n'est pas nécessaire.`}
                    data-testid={`batch-lot-similar-${l.lot_id}`}
                  >
                    <Copy className="w-2.5 h-2.5" />
                    ≈ {l.correspondance}
                  </span>
                )}
                {gens && gens.count > 0 && (
                  <span
                    className="inline-flex items-center gap-0.5 text-[9.5px] font-semibold px-1 py-0.5 rounded-full bg-emerald-100 text-emerald-700 flex-shrink-0"
                    title={`Déjà généré ${gens.count}×`}
                  >
                    <CheckCircle2 className="w-2.5 h-2.5" />
                    {gens.count}
                  </span>
                )}
              </label>
            );
          })
        )}
      </div>
      {selectedIds.size > 0 && (
        <p className="text-[10.5px] text-amber-700" data-testid="batch-lot-summary">
          {selectedIds.size} lot{selectedIds.size > 1 ? 's' : ''} sélectionné{selectedIds.size > 1 ? 's' : ''} · partageront les mêmes paramètres
        </p>
      )}
    </div>
  );
};

export default GenerationPage;
